import { ShoppingBag } from 'lucide-react';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';
import { Bloco, BlocoComoFalar, BlocoPorQueLigar, BlocoQuemE, Par, Tile, Tiles, cnpjFmt, cpfFmt, moeda, telefoneFmt } from './lead-cpf-blocos';

interface Props {
  lead: LeadCarteira;
  onLigar?: (telefone: string) => void;
}

interface ItemComprado { produto?: string; plano?: string; valor?: number; quantidade?: number; linha?: string }

const dataFmt = (s: unknown) => {
  const d = new Date(String(s ?? ''));
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

// Retenção CPF (Douglas 27/09/2026): o cliente fez o pedido pela empresa e ele
// não seguiu. A gente leva o mesmo para o CPF dele — sempre com a fibra, que é o
// que permite o móvel no CPF. Mesmo esqueleto das abas de Portabilidade e
// Renovação CPF, com o conteúdo da retenção.
export function LeadDados360RetencaoCpf({ lead, onLigar }: Props) {
  const m = (lead.dados_master_lead || {}) as Record<string, unknown>;
  const itens = (Array.isArray(m.comprou_no_cnpj) ? m.comprou_no_cnpj : []) as ItemComprado[];
  const total = itens.reduce((s, i) => s + (Number(i.valor) || 0), 0);
  const temFibra = itens.some((i) => /fibra|banda larga/i.test(`${i.produto} ${i.plano}`));
  const moveis = itens.filter((i) => !/fibra|banda larga|tv|mesh/i.test(`${i.produto} ${i.plano}`));
  const aprovado = m.credito_aprovado === true;
  const empresa = String(m.razao_social_origem || '').trim() || 'a empresa';
  const telefones = [lead.contato_real_telefone, lead.telefone].map((t) => String(t ?? '').replace(/\D/g, '')).filter((t, i, a) => t.length >= 10 && a.indexOf(t) === i);

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(290px,1fr))] gap-3">
      <BlocoPorQueLigar
        meta={`cancelado em ${dataFmt(m.cancelado_em) ?? '—'}`}
        frase={aprovado
          ? `O pedido da ${empresa} foi aprovado pela Claro e não chegou a ser entregue. Dá para entregar no CPF dele.`
          : `O pedido da Claro que a ${empresa} fez com a gente não seguiu. Dá para fazer o mesmo no CPF dele.`}
      >
        <p className="text-[12.5px] text-muted-foreground">
          {temFibra
            ? 'Tinha fibra no pedido: a fibra no CPF abre o móvel no mesmo pacote.'
            : 'Não tinha fibra: no CPF o móvel só entra com fibra Claro — a que ele já tem ou a que comprar agora.'}
        </p>
      </BlocoPorQueLigar>

      <Bloco titulo="O que ele comprou" meta={`${itens.length} ${itens.length === 1 ? 'item' : 'itens'} · no CNPJ`} icone={<ShoppingBag className="h-3 w-3" />}>
        <Tiles>
          <Tile rotulo="total no CNPJ" valor={moeda(total)} />
          <Tile rotulo="linhas móveis" valor={String(moveis.length)} />
          <Tile rotulo="fibra" valor={temFibra ? 'sim' : 'não'} />
        </Tiles>
        <ul className="space-y-1">
          {itens.map((i, k) => (
            <li key={k} className="grid grid-cols-[minmax(0,1fr)_88px] gap-2 text-[13px]">
              <span className="truncate" title={`${i.plano || i.produto}${i.linha ? ` · ${i.linha}` : ''}`}>
                {i.plano || i.produto}
                {i.linha ? <span className="text-muted-foreground"> · {telefoneFmt(i.linha)}</span> : null}
              </span>
              <span className="text-right font-display tabular-nums">{moeda(Number(i.valor) || 0)}</span>
            </li>
          ))}
        </ul>
      </Bloco>

      <Bloco titulo="Por que não seguiu" meta={String(m.pedidos_cancelados || '')}>
        <Par rotulo="situação do pedido" valor={String(m.motivo_cancelamento || '—')} />
        <Par rotulo="crédito na Claro" valor={aprovado ? 'aprovado antes de cancelar' : 'não chegou a ser aprovado'} />
        <p className="text-[12px] text-muted-foreground">Não se discute o motivo com o cliente: a conversa é sobre entregar agora.</p>
      </Bloco>

      <BlocoComoFalar
        master={{ gestor_nome: lead.contato_real_nome, gestor_celular: telefones[0] ?? null, gestor_email: lead.contato_real_email ?? lead.email }}
        telefones={telefones}
        email={lead.email}
        onLigar={onLigar}
      />

      <BlocoQuemE meta={String(m.cpf_fonte || 'titular a confirmar')}>
        <Par rotulo="titular" valor={lead.nome_proprietario || lead.razao_social || '—'} />
        <Par rotulo="CPF" valor={lead.cpf ? cpfFmt(lead.cpf) : 'a confirmar'} />
        <Par rotulo="empresa de origem" valor={`${empresa}${m.cnpj_origem ? ` · ${cnpjFmt(String(m.cnpj_origem))}` : ''}`} />
        <Par rotulo="atenção" valor="o CPF pode ser de quem assinou pela empresa — confirme o titular" />
      </BlocoQuemE>
    </div>
  );
}
